import { useEffect, useState } from 'react';
import SectionHead from '../components/SectionHead';
import Reveal from '../components/Reveal';
import usePageMeta from '../hooks/usePageMeta';
import { CONSENT_CHANGED, CONSENT_KEY } from '../analytics/ga4';

const LABEL = 'font-mono text-[10px] uppercase tracking-[0.25em] text-accentText';
const BODY = 'text-sm leading-relaxed text-dim';
const CARD = 'border border-line bg-base p-7 sm:p-8';
const BTN = 'inline-flex items-center gap-2 border px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors';

const DEFAULT_PREFS = { analytics: false };

function Toggle({ on, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      className={`relative h-6 w-11 shrink-0 border transition-colors ${on ? 'border-accent bg-accent' : 'border-line2 bg-panel'}`}
      data-testid={testid}
    >
      <span
        className={`absolute top-0.5 bg-white transition-all ${on ? 'left-[22px]' : 'left-0.5'}`}
        style={{ height: 18, width: 18 }}
        aria-hidden="true"
      />
    </button>
  );
}

export default function CookiePreferences() {
  usePageMeta(
    'Cookie Preferences | RangeSafe Commercial Solutions',
    'Choose which non-essential cookies the RangeSafe commercial website may use.'
  );
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [savedAt, setSavedAt] = useState(null);
  const [justSaved, setJustSaved] = useState(false);
  const [gpc, setGpc] = useState(false);

  useEffect(() => {
    setGpc(navigator.globalPrivacyControl === true);
    try {
      const raw = window.localStorage.getItem(CONSENT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setPrefs({ analytics: Boolean(parsed.analytics) });
        setSavedAt(parsed.updatedAt || null);
      }
    } catch {
      /* no saved preferences */
    }
  }, []);

  const save = (next) => {
    const updatedAt = new Date().toISOString();
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...next, essential: true, marketing: false, updatedAt }));
    window.dispatchEvent(new Event(CONSENT_CHANGED));
    setPrefs(next);
    setSavedAt(updatedAt);
    setJustSaved(true);
    window.setTimeout(() => setJustSaved(false), 3000);
  };

  return (
    <main className="pt-16">
      <header className="mx-auto max-w-shell px-5 pb-16 pt-20 sm:px-8 lg:pt-28">
        <SectionHead
          as="h1"
          eyebrow="Legal"
          lines={['Cookie Preferences']}
          lead="Choose whether this website may use analytics cookies. Your choice is saved in this browser and applies to future visits. Google Analytics loads only when analytics is switched on."
        />
      </header>

      <div className="mx-auto max-w-shell px-5 pb-24 sm:px-8" data-testid="cookie-preferences">
        <div className="grid max-w-4xl gap-6">
          <Reveal className={CARD}>
            <div className="flex items-start justify-between gap-6" data-testid="cookie-essential">
              <div>
                <p className={LABEL}>Strictly necessary</p>
                <p className={`mt-3 ${BODY}`}>
                  Required for the website to function — including remembering the choices you make on this page.
                  These cannot be switched off.
                </p>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute">Always on</span>
            </div>
          </Reveal>

          <Reveal delay={0.05} className={CARD}>
            <div className="flex items-start justify-between gap-6" data-testid="cookie-analytics">
              <div>
                <p className={LABEL}>Analytics &amp; performance</p>
                <p className={`mt-3 ${BODY}`}>
                  Allows Google Analytics to count visits and see which pages are viewed so we can improve the site.
                  Google Analytics loads only when this is switched on.
                </p>
              </div>
              <Toggle on={prefs.analytics} onChange={(v) => setPrefs({ ...prefs, analytics: v })} testid="cookie-analytics-toggle" />
            </div>
            {gpc && (
              <p className="mt-4 border-t border-line2 pt-4 text-xs leading-relaxed text-mute" data-testid="gpc-notice">
                Your browser is sending a Global Privacy Control (GPC) signal, so analytics cookies stay off on
                this website regardless of the setting above.
              </p>
            )}
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-8 flex flex-wrap gap-4">
            <button
              type="button"
              className={`${BTN} border-accent bg-accent text-white hover:brightness-110`}
              onClick={() => save({ analytics: true })}
              data-testid="cookie-accept-all"
            >
              Accept analytics
            </button>
            <button
              type="button"
              className={`${BTN} border-line text-white hover:border-accent`}
              onClick={() => save({ analytics: false })}
              data-testid="cookie-decline-all"
            >
              Decline non-essential
            </button>
            <button
              type="button"
              className={`${BTN} border-line text-white hover:border-accent`}
              onClick={() => save(prefs)}
              data-testid="cookie-save"
            >
              Save choices
            </button>
          </div>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-mute" data-testid="cookie-status">
            {justSaved
              ? 'Your choices have been saved.'
              : savedAt
                ? `Last saved ${new Date(savedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.`
                : 'No choices saved yet — strictly necessary cookies only.'}
          </p>
        </Reveal>
      </div>
    </main>
  );
}
